import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { Copy, Check, Trash2, Eye, Code } from "lucide-react";

// ── Simple Markdown renderer ─────────────────────────────────

function renderMarkdown(md: string): string {
    let html = md;

    // Code blocks (fenced)
    html = html.replace(/```(\w*)\n([\s\S]*?)```/g, (_m, lang, code) => {
        const escaped = code.replace(/</g, "&lt;").replace(/>/g, "&gt;").trimEnd();
        return `<pre class="md-pre"><code class="md-code${lang ? ` lang-${lang}` : ""}">${escaped}</code></pre>`;
    });

    // Inline code
    html = html.replace(/`([^`]+)`/g, '<code class="md-inline-code">$1</code>');

    // Headers
    html = html.replace(/^######\s(.+)$/gm, '<h6 class="md-h6">$1</h6>');
    html = html.replace(/^#####\s(.+)$/gm, '<h5 class="md-h5">$1</h5>');
    html = html.replace(/^####\s(.+)$/gm, '<h4 class="md-h4">$1</h4>');
    html = html.replace(/^###\s(.+)$/gm, '<h3 class="md-h3">$1</h3>');
    html = html.replace(/^##\s(.+)$/gm, '<h2 class="md-h2">$1</h2>');
    html = html.replace(/^#\s(.+)$/gm, '<h1 class="md-h1">$1</h1>');

    // Bold and italic
    html = html.replace(/\*\*\*(.+?)\*\*\*/g, '<strong><em>$1</em></strong>');
    html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
    html = html.replace(/\*(.+?)\*/g, '<em>$1</em>');

    // Strikethrough
    html = html.replace(/~~(.+?)~~/g, '<del>$1</del>');

    // Links
    html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" class="md-link" target="_blank" rel="noopener">$1</a>');

    // Images
    html = html.replace(/!\[([^\]]*)\]\(([^)]+)\)/g, '<img src="$2" alt="$1" class="md-img" />');

    // Blockquotes
    html = html.replace(/^>\s(.+)$/gm, '<blockquote class="md-blockquote">$1</blockquote>');

    // Horizontal rule
    html = html.replace(/^---$/gm, '<hr class="md-hr" />');

    // Unordered lists
    html = html.replace(/^[*-]\s(.+)$/gm, '<li class="md-li">$1</li>');
    html = html.replace(/((?:<li class="md-li">.*<\/li>\n?)+)/g, '<ul class="md-ul">$1</ul>');

    // Ordered lists
    html = html.replace(/^\d+\.\s(.+)$/gm, '<li class="md-oli">$1</li>');
    html = html.replace(/((?:<li class="md-oli">.*<\/li>\n?)+)/g, '<ol class="md-ol">$1</ol>');

    // Task lists
    html = html.replace(/<li class="md-li">\[x\]\s(.*?)<\/li>/gi, '<li class="md-li md-task md-checked">$1</li>');
    html = html.replace(/<li class="md-li">\[\s?\]\s(.*?)<\/li>/gi, '<li class="md-li md-task">$1</li>');

    // Tables
    html = html.replace(
        /^(\|.+\|)\n(\|[-:\s|]+\|)\n((?:\|.+\|\n?)+)/gm,
        (_m: string, headerRow: string, _sep: string, bodyRows: string) => {
            const headers = headerRow.split("|").filter(Boolean).map((h: string) => `<th class="md-th">${h.trim()}</th>`).join("");
            const rows = bodyRows.trim().split("\n").map((row: string) => {
                const cells = row.split("|").filter(Boolean).map((c: string) => `<td class="md-td">${c.trim()}</td>`).join("");
                return `<tr>${cells}</tr>`;
            }).join("");
            return `<table class="md-table"><thead><tr>${headers}</tr></thead><tbody>${rows}</tbody></table>`;
        },
    );

    // Paragraphs — wrap remaining bare lines
    html = html.replace(/^(?!<[a-z]|$)(.+)$/gm, '<p class="md-p">$1</p>');

    return html;
}

const DEFAULT_MARKDOWN = `# Hello Markdown

This is a **live preview** of your markdown. Try editing the left panel!

## Features

- **Bold**, *italic*, and ~~strikethrough~~ text
- [Links](https://ballahcode.com) and images
- Code blocks with syntax highlighting

### Code Example

\`\`\`javascript
function greet(name) {
    return \`Hello, \${name}!\`;
}
\`\`\`

> Blockquotes work too. Great for callouts.

### Task List

- [x] Create markdown parser
- [x] Add live preview
- [ ] Ship it!

| Feature | Status |
|---------|--------|
| Headers | ✅ |
| Lists   | ✅ |
| Tables  | ✅ |

---

Built with ❤️ by Ballah Code.
`;

// ── Component ────────────────────────────────────────────────

export function MarkdownPreview() {
    const [input, setInput] = useState(DEFAULT_MARKDOWN);
    const [copied, setCopied] = useState<string | null>(null);
    const [view, setView] = useState<"split" | "preview">("split");

    useEffect(() => {
        document.title = "Markdown Preview — Live Editor & Renderer — Ballah Code";
        const meta = document.querySelector('meta[name="description"]');
        if (meta) {
            meta.setAttribute(
                "content",
                "Free online markdown editor with live preview. Supports GitHub Flavored Markdown with tables, task lists, code blocks and more.",
            );
        }
    }, []);

    const rendered = useMemo(() => renderMarkdown(input), [input]);

    const copy = async (val: string, key: string) => {
        await navigator.clipboard.writeText(val);
        setCopied(key);
        setTimeout(() => setCopied(null), 2000);
    };

    return (
        <div className="min-h-screen bg-background text-foreground font-sans">
            <nav className="flex items-center justify-between px-8 py-[18px]">
                <Link to="/" className="text-sm font-semibold tracking-[-0.03em] text-zinc-200 hover:text-white transition-colors">ballah code</Link>
                <div className="flex items-center gap-6 text-[13px] text-zinc-600">
                    <Link to="/tools" className="hover:text-zinc-400 transition-colors">tools</Link>
                    <Link to="/" className="hover:text-zinc-400 transition-colors">home</Link>
                </div>
            </nav>

            <main className="max-w-[1280px] mx-auto px-8 pt-12 pb-24">
                <div className="flex items-center gap-2 text-[13px] text-zinc-600 mb-8">
                    <Link to="/tools" className="hover:text-zinc-400 transition-colors">Tools</Link>
                    <span className="text-zinc-700">/</span>
                    <span className="text-zinc-400">Markdown Preview</span>
                </div>

                <div className="mb-8">
                    <h1 className="text-3xl font-medium tracking-[-0.03em] mb-3">Markdown Preview</h1>
                    <p className="text-zinc-500 text-[15px] leading-relaxed max-w-2xl">
                        Write markdown on the left, see the rendered output on the right. Supports headers, lists, tables, code blocks, and more.
                    </p>
                </div>

                {/* Controls */}
                <div className="flex items-center gap-2 mb-4">
                    <div className="flex rounded-lg border border-white/[0.06] overflow-hidden">
                        <button onClick={() => setView("split")} className={`px-3 py-2 text-[12px] flex items-center gap-1.5 transition-colors ${view === "split" ? "bg-white/[0.08] text-zinc-200" : "text-zinc-600 hover:text-zinc-400"}`}>
                            <Code size={12} /> Split
                        </button>
                        <button onClick={() => setView("preview")} className={`px-3 py-2 text-[12px] flex items-center gap-1.5 transition-colors ${view === "preview" ? "bg-white/[0.08] text-zinc-200" : "text-zinc-600 hover:text-zinc-400"}`}>
                            <Eye size={12} /> Preview
                        </button>
                    </div>

                    <button onClick={() => copy(input, "md")} className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-white/[0.06] text-[12px] text-zinc-500 hover:text-zinc-300 transition-colors ml-auto">
                        {copied === "md" ? <Check size={12} className="text-green-400" /> : <Copy size={12} />}
                        Copy MD
                    </button>
                    <button onClick={() => copy(rendered, "html")} className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-white/[0.06] text-[12px] text-zinc-500 hover:text-zinc-300 transition-colors">
                        {copied === "html" ? <Check size={12} className="text-green-400" /> : <Copy size={12} />}
                        Copy HTML
                    </button>
                    {input && (
                        <button onClick={() => setInput("")} className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-white/[0.06] text-[12px] text-zinc-600 hover:text-zinc-400 transition-colors">
                            <Trash2 size={12} /> Clear
                        </button>
                    )}
                </div>

                {/* Editor + Preview */}
                <div className={`grid gap-4 mb-16 ${view === "split" ? "grid-cols-1 lg:grid-cols-2" : "grid-cols-1"}`}>
                    {view === "split" && (
                        <div className="flex flex-col">
                            <div className="px-4 py-2.5 bg-[#0d0d0f] border border-white/[0.06] border-b-0 rounded-t-xl">
                                <span className="text-[11px] text-zinc-600 font-mono uppercase tracking-wider">Markdown</span>
                            </div>
                            <textarea
                                value={input}
                                onChange={(e) => setInput(e.target.value)}
                                placeholder="Type markdown here..."
                                spellCheck={false}
                                className="w-full min-h-[500px] bg-[#111113] border border-white/[0.06] rounded-b-xl p-4 text-[13px] text-zinc-300 font-mono leading-relaxed outline-none resize-y placeholder:text-zinc-800 focus:border-white/[0.1] transition-colors"
                            />
                        </div>
                    )}

                    <div className="flex flex-col">
                        <div className="px-4 py-2.5 bg-[#0d0d0f] border border-white/[0.06] border-b-0 rounded-t-xl">
                            <span className="text-[11px] text-zinc-600 font-mono uppercase tracking-wider">Preview</span>
                        </div>
                        <div
                            className="w-full min-h-[500px] bg-[#111113] border border-white/[0.06] rounded-b-xl p-6 overflow-auto md-preview"
                            dangerouslySetInnerHTML={{ __html: rendered }}
                        />
                    </div>
                </div>

                {/* SEO */}
                <section className="max-w-2xl border-t border-white/[0.04] pt-12">
                    <h2 className="text-lg font-medium tracking-[-0.02em] mb-4 text-zinc-300">About Markdown</h2>
                    <div className="space-y-4 text-[14.5px] text-zinc-500 leading-[1.75]">
                        <p>Markdown is a lightweight markup language created by John Gruber. It lets you write formatted text using plain text syntax — headers with #, bold with **, lists with -, and more. It's the standard for README files, documentation, and developer communication.</p>
                        <p>This tool renders a subset of GitHub Flavored Markdown (GFM) including tables, task lists, fenced code blocks, strikethrough, and blockquotes. Everything runs in your browser with no external dependencies.</p>
                        <p>Built by the team behind <Link to="/" className="text-violet-400 hover:text-violet-300 transition-colors">Ballah Code</Link>.</p>
                    </div>
                    <div className="mt-8 flex flex-wrap gap-2">
                        <span className="text-[12px] text-zinc-700 mr-1 self-center">Related →</span>
                        <Link to="/tools/diff-checker" className="text-[12px] text-zinc-500 hover:text-violet-400 transition-colors px-2.5 py-1 rounded-md border border-white/[0.06] hover:border-violet-400/30">Diff Checker</Link>
                        <Link to="/tools/json-formatter" className="text-[12px] text-zinc-500 hover:text-violet-400 transition-colors px-2.5 py-1 rounded-md border border-white/[0.06] hover:border-violet-400/30">JSON Formatter</Link>
                        <Link to="/tools/regex-tester" className="text-[12px] text-zinc-500 hover:text-violet-400 transition-colors px-2.5 py-1 rounded-md border border-white/[0.06] hover:border-violet-400/30">Regex Tester</Link>
                    </div>
                </section>
            </main>

            <footer className="border-t border-white/[0.04] py-5 px-8 flex justify-between max-w-[1280px] mx-auto text-xs text-zinc-800">
                <Link to="/" className="hover:text-zinc-600 transition-colors">ballah code</Link>
                <Link to="/tools" className="hover:text-zinc-600 transition-colors">tools</Link>
                <span>ballah tech</span>
            </footer>

            <style>{`
                .md-preview { color: #a1a1aa; font-size: 14px; line-height: 1.8; }
                .md-preview .md-h1 { font-size: 1.75em; font-weight: 600; color: #e4e4e7; margin: 0.8em 0 0.4em; border-bottom: 1px solid rgba(255,255,255,0.06); padding-bottom: 0.3em; }
                .md-preview .md-h2 { font-size: 1.4em; font-weight: 600; color: #e4e4e7; margin: 0.8em 0 0.4em; border-bottom: 1px solid rgba(255,255,255,0.04); padding-bottom: 0.2em; }
                .md-preview .md-h3 { font-size: 1.15em; font-weight: 600; color: #d4d4d8; margin: 0.6em 0 0.3em; }
                .md-preview .md-h4, .md-preview .md-h5, .md-preview .md-h6 { font-size: 1em; font-weight: 600; color: #d4d4d8; margin: 0.5em 0 0.2em; }
                .md-preview .md-p { margin: 0.5em 0; }
                .md-preview strong { color: #d4d4d8; font-weight: 600; }
                .md-preview em { font-style: italic; }
                .md-preview del { text-decoration: line-through; opacity: 0.6; }
                .md-preview .md-link { color: #a78bfa; text-decoration: none; }
                .md-preview .md-link:hover { color: #c4b5fd; text-decoration: underline; }
                .md-preview .md-blockquote { border-left: 3px solid rgba(167,139,250,0.3); padding: 0.3em 1em; margin: 0.5em 0; color: #71717a; }
                .md-preview .md-pre { background: #0a0a0b; border: 1px solid rgba(255,255,255,0.06); border-radius: 8px; padding: 1em; margin: 0.8em 0; overflow-x: auto; }
                .md-preview .md-code { font-family: ui-monospace, monospace; font-size: 0.9em; color: #a1a1aa; }
                .md-preview .md-inline-code { background: rgba(255,255,255,0.06); padding: 0.15em 0.4em; border-radius: 4px; font-family: ui-monospace, monospace; font-size: 0.9em; color: #c4b5fd; }
                .md-preview .md-ul, .md-preview .md-ol { margin: 0.5em 0; padding-left: 1.5em; }
                .md-preview .md-li, .md-preview .md-oli { margin: 0.2em 0; }
                .md-preview .md-task { list-style: none; margin-left: -1.2em; }
                .md-preview .md-task::before { content: "☐ "; opacity: 0.5; }
                .md-preview .md-checked::before { content: "☑ "; color: #a78bfa; opacity: 1; }
                .md-preview .md-hr { border: none; border-top: 1px solid rgba(255,255,255,0.06); margin: 1.5em 0; }
                .md-preview .md-table { width: 100%; border-collapse: collapse; margin: 0.8em 0; font-size: 0.9em; }
                .md-preview .md-th { text-align: left; padding: 0.5em 0.8em; border-bottom: 2px solid rgba(255,255,255,0.08); color: #d4d4d8; font-weight: 600; }
                .md-preview .md-td { padding: 0.4em 0.8em; border-bottom: 1px solid rgba(255,255,255,0.04); }
                .md-preview .md-img { max-width: 100%; border-radius: 8px; margin: 0.5em 0; }
            `}</style>
        </div>
    );
}
